import 'ace-builds';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/mode-text';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/ext-searchbox';
import 'ace-builds/src-noconflict/theme-github';

import * as React from 'react';
import {useEffect, useRef} from 'react';
import AceEditor, {IMarker} from 'react-ace';

import {Button} from '~/components/Button';
import {IInputFieldProps} from '~/components/Form';
import {Intent} from '~/lib/intent';
import {useCopyToClipboard} from '~/lib/useCopyToClipboard';
import style from './index.module.scss';

export interface IEditorProps extends IInputFieldProps<string> {
    mode?: string;
    theme?: string;
    disabled?: boolean;
    maxLines?: number;
    minLines?: number;
    className?: string;
    canFullScreen?: boolean;
    canCopy?: boolean;
    markers?: IMarker[];
    transform?: (value: string) => string;
    onCopy?: () => void;
}

export function EditorInput({
    canFullScreen = false,
    canCopy = false,
    className,
    disabled = false,
    maxLines,
    minLines,
    markers,
    mode,
    theme,
    id,
    value,
    transform,
    onChange,
    onCopy
}: IEditorProps) {
    const [isFullScreen, setFullScreen] = React.useState(false);
    const [copied, copy] = useCopyToClipboard();

    const editor = useRef<HTMLDivElement | null>(null);

    const onFullScreenChange = () => {
        if (document.fullscreenElement) {
            setFullScreen(true);
        } else {
            setFullScreen(false);
        }
    };

    useEffect(() => {
        const current = editor.current;

        if (current) {
            current.addEventListener('fullscreenchange', onFullScreenChange);
        }
        return () => {
            if (current) {
                current.removeEventListener('fullscreenchange', onFullScreenChange);
            }
        };
    }, []);

    const handleChange = (value: string) => {
        if (transform) {
            onChange?.(transform(value));
        } else {
            onChange?.(value);
        }
    };

    let classes = [style.editorWrapper, className];
    if (disabled) {
        classes.push(style.disabled);
    }
    if (isFullScreen) {
        classes.push(style.isFullScreen);
    }

    return <div className={classes.join(' ')} ref={editor}>
        {(!isFullScreen && canFullScreen) &&
            <Button
                onClick={() => editor.current && editor.current.requestFullscreen()}
                className={style.fullScreenButton}
                leftIcon={<i className='fa fa-arrows-alt'/>}
                small
                title='Full screen'
                aria-label='Full screen'
            />}
        {canCopy && <Button
            onClick={() => {
                copy(value ?? '');
                onCopy?.();
            }}
            className={style.copyButton}
            intent={copied ? Intent.SUCCESS : undefined}
            leftIcon={<i className={`fa fa-${copied ? 'check' : 'copy'}`}/>}
            small
            title='Copy'
            aria-label='Copy'
        />}
        <AceEditor
            mode={mode || 'text'}
            theme={theme || 'github'}
            maxLines={maxLines}
            minLines={minLines}
            markers={markers}
            value={value}
            readOnly={disabled}
            onChange={handleChange}
            name={id}
            width='100%'
            wrapEnabled
            setOptions={{
                printMargin: false,
                useWorker: false,
            }}
        />
    </div>;
}
